/**
 * useShellImpacts - IMPACTS (v8.326.0): what just happened to somebody.
 *
 * Read off the logs and worn by the marker and the turn card for a moment.
 * High-water marks start at mount, so a history never replays; the list is
 * capped at twenty and pruned on a slow tick while anything is alive.
 * Returns the live impacts by combatant. Extracted from DeathWatchSimulator.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { collectImpacts, liveImpacts, IMPACT_TTL_MS } from '../engine/impacts';
import { noteSettled } from '../diagnostics/beatLog';
import { notePlateStale } from '../diagnostics/plateLog';

/**
 * Fresh impacts appended to the still-live ones, the newest twenty kept. One
 * attack heard twice - the attacker's own log, then the shared copy, stamped
 * again - is one impact: the same id, the line's own time (engine/attackBeat).
 */
export function withFresh(prev, fresh, now) {
  const kept = prev.filter((i) => now - i.at <= IMPACT_TTL_MS);
  for (const i of fresh) if (!kept.some((k) => k.id === i.id)) kept.push(i);
  return kept.slice(-20);
}

/** @param {object[]} tabs @param {object[]|undefined} firestoreNarrative @param {object[]} roster */
export default function useShellImpacts(tabs, firestoreNarrative, roster) {
  const impactMarks = useRef({ local: {}, shared: 0 });
  const [impacts, setImpacts] = useState([]);
  useEffect(() => {
    const now = Date.now();
    const { impacts: fresh, marks } = collectImpacts(tabs, firestoreNarrative ?? [], roster, impactMarks.current, now);
    impactMarks.current = marks;
    // Heard of only after it had expired (a muted or offline screen catching up): no flash, no beat, no play -
    // the fuzzer's beat and plate oracles are told so.
    for (const i of fresh) {
      if (now - i.at <= IMPACT_TTL_MS) continue;
      noteSettled(i.id, now);
      notePlateStale(i.id, now);
    }
    if (fresh.length) setImpacts((prev) => withFresh(prev, fresh, now));
  }, [tabs, firestoreNarrative, roster]);
  useEffect(() => {
    if (!impacts.length) return undefined;
    const t = setTimeout(
      () => setImpacts((prev) => prev.filter((i) => Date.now() - i.at <= IMPACT_TTL_MS)),
      IMPACT_TTL_MS + 100,
    );
    return () => clearTimeout(t);
  }, [impacts]);
  return useMemo(() => liveImpacts(impacts, Date.now()), [impacts]);
}
